/* eslint-disable @typescript-eslint/no-explicit-any -- dynamic PostgREST/Supabase rows are shaped at runtime; typed at the boundary. TODO: replace with generated row types. */
/**
 * useVideoRefs — pasted IG (etc.) video references. List + add + delete
 * via /api/content/video-ref. Preview via IG's public /embed route,
 * no download needed.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface VideoRef {
  id: string;
  source_url: string;
  provider: string;
  shortcode: string | null;
  bucket_slug: string | null;
  notes: string | null;
  author_handle: string | null;
  caption: string | null;
  b2_video_key: string | null;   // populated only after RapidAPI download
  b2_thumb_key: string | null;
  extracted_style_prompt: string | null;
  extracted_at: string | null;
  added_at: string;
  added_via: string;
}

export interface GeneratedVideo {
  id: string;
  source_ref_id: string | null;
  prompt: string;
  b2_video_key: string | null;
  duration_s: number | null;
  aspect_ratio: string | null;
  model: string | null;
  status: 'queued' | 'running' | 'done' | 'failed';
  error: string | null;
  generated_ms: number | null;
  created_at: string;
  finished_at: string | null;
  reel_id: string | null;
  shot_index: number | null;
  shot_total: number | null;
  shot_description: string | null;
}

export interface SavedStyle {
  id: string;
  name: string;
  source_ref_id: string | null;
  prompt: string;
  bucket_slug: string | null;
  thumb_b2_key: string | null;
  notes: string | null;
  usage_count: number;
  saved_at: string;
  last_used_at: string | null;
}

export function useSavedStyles(bucketSlug: string | null = null) {
  return useQuery<SavedStyle[]>({
    queryKey: ['saved-styles', bucketSlug],
    queryFn: async () => {
      let q = marketing
        .from('content_video_style')
        .select('id,name,source_ref_id,prompt,bucket_slug,thumb_b2_key,notes,usage_count,saved_at,last_used_at')
        .order('saved_at', { ascending: false })
        .limit(50);
      if (bucketSlug) q = q.eq('bucket_slug', bucketSlug);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as SavedStyle[];
    },
    staleTime: 30_000,
  });
}

export function useSaveStyle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; source_ref_id: string; prompt: string; bucket_slug?: string | null; thumb_b2_key?: string | null; notes?: string }) => {
      const { data, error } = await marketing.from('content_video_style').insert({
        name: input.name,
        source_ref_id: input.source_ref_id,
        prompt: input.prompt,
        bucket_slug: input.bucket_slug ?? null,
        thumb_b2_key: input.thumb_b2_key ?? null,
        notes: input.notes ?? null,
      }).select().single();
      if (error) throw error;
      return data as SavedStyle;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['saved-styles'] }),
  });
}

export function useDeleteSavedStyle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await marketing.from('content_video_style').delete().eq('id', id);
      if (error) throw error;
      return { ok: true };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['saved-styles'] }),
  });
}

export interface ReelShot {
  shot_index: number;
  duration_s: number;
  beat: string;
  prompt: string;
  handoff_state: string;
}

export interface ReelPlan {
  ok: true;
  ref_id: string;
  target_seconds: number;
  shot_count: number;
  per_shot_seconds: number;
  narrative: string;
  continuity_anchors: Record<string, unknown>;
  shots: ReelShot[];
}

export function useVideoRefs(bucketSlug: string | null = null) {
  return useQuery<VideoRef[]>({
    queryKey: ['video-refs', bucketSlug],
    queryFn: async () => {
      let q = marketing
        .from('content_video_reference')
        .select('id,source_url,provider,shortcode,bucket_slug,notes,author_handle,caption,b2_video_key,b2_thumb_key,extracted_style_prompt,extracted_at,added_at,added_via')
        .order('added_at', { ascending: false })
        .limit(60);
      if (bucketSlug) q = q.eq('bucket_slug', bucketSlug);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as VideoRef[];
    },
    staleTime: 30_000,
  });
}

export function useAddVideoRef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { url: string; bucket_slug?: string | null; notes?: string }) => {
      return await fetchJsonWithRetry('/api/content/video-ref', { action: 'add', ...input }) as { ok: true; ref: VideoRef; embed_url: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['video-refs'] }),
  });
}

export function useDeleteVideoRef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await fetchJsonWithRetry('/api/content/video-ref', { action: 'delete', id });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['video-refs'] }),
  });
}

export function useDownloadVideoRef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await fetchJsonWithRetry('/api/content/video-ref', { action: 'download', id });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['video-refs'] }),
  });
}

/**
 * Fire-and-poll — kick off the extract job, ignore the HTTP response
 * (CF Pages times out at 30s and serves HTML), instead poll the DB row
 * for `extracted_at` change. Supabase edge keeps running for full 5 min.
 */
/**
 * Fires the extract request and polls the DB row. CF Pages proxy can 502
 * transiently (30s wall-clock, cold worker, redeploy) even while the
 * Supabase edge function completes normally — so proxy errors are noted
 * but don't abort the poll. We also retry the fetch once at 30s if the
 * first attempt failed AND no result has arrived.
 */
export function useExtractStyle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: before } = await marketing.from('content_video_reference').select('extracted_at').eq('id', id).single();
      const beforeAt = before?.extracted_at ?? null;

      let lastFetchErr: string | null = null;
      let attempts = 0;
      const fire = () => {
        attempts++;
        return fetch('/api/content/video-ref-extract-style', {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ id }),
        }).then(async (r) => {
          const ct = r.headers.get('content-type') ?? '';
          const bodyText = await r.text().catch(() => '');
          if (r.ok && ct.includes('json')) { lastFetchErr = null; return; }
          // Try to surface the real error message from the JSON body
          let detail = '';
          if (ct.includes('json')) {
            try {
              const b = JSON.parse(bodyText);
              detail = b.step ? `${b.step}: ${b.error ?? JSON.stringify(b).slice(0, 200)}` : (b.error ?? b.detail ?? bodyText.slice(0, 200));
            } catch { detail = bodyText.slice(0, 200); }
          } else {
            detail = bodyText.slice(0, 100).replace(/\s+/g, ' ');
          }
          lastFetchErr = `edge ${r.status}: ${detail}`;
        }).catch((e) => { lastFetchErr = `edge proxy: ${e.message}`; });
      };
      fire();

      const t0 = Date.now();
      const deadline = t0 + 180_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2_000));
        const { data } = await marketing.from('content_video_reference').select('extracted_at,extracted_style_prompt,extracted_ms').eq('id', id).single();
        if (data?.extracted_at && data.extracted_at !== beforeAt) {
          return { ok: true, prompt: data.extracted_style_prompt, extracted_ms: data.extracted_ms ?? 0 } as any;
        }
        // Retry once at 30s if first fetch failed and no result yet
        if (lastFetchErr && attempts < 2 && Date.now() - t0 > 30_000) {
          fire();
        }
      }
      throw new Error(lastFetchErr ? `${lastFetchErr} + timed out after 180s` : 'extract timed out after 180s — check Supabase logs');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['video-refs'] }),
  });
}

/**
 * Retry helper for one-shot proxied Claude calls (preview, plan-shots).
 * CF Pages proxy 502s / non-JSON responses get one retry with 2s backoff.
 */
async function fetchJsonWithRetry(url: string, body: unknown, tries = 2): Promise<any> {
  let lastErr: Error | null = null;
  for (let attempt = 0; attempt < tries; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 2_000));
    try {
      const r = await fetch(url, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const ct = r.headers.get('content-type') ?? '';
      const bodyText = await r.text().catch(() => '');
      if (r.ok && ct.includes('json')) {
        try { return JSON.parse(bodyText); } catch { /* fall through */ }
      }
      // Extract the actual error from the JSON body (v5+ edge fns return {step, error, stack})
      let detail = '';
      if (ct.includes('json')) {
        try {
          const b = JSON.parse(bodyText);
          if (r.ok && !b.error) return b;
          detail = b.step ? `${b.step}: ${b.error ?? JSON.stringify(b).slice(0, 200)}` : (b.hint ?? b.error ?? b.detail ?? bodyText.slice(0, 200));
        } catch { detail = bodyText.slice(0, 200); }
      } else {
        detail = bodyText.slice(0, 120).replace(/\s+/g, ' ');
      }
      lastErr = new Error(`edge ${r.status}: ${detail || 'no body'}`);
      // Retry 502/504 (proxy issues); don't retry 4xx (client errors) or 500 with detail
      if (r.status < 500 || (r.status === 500 && detail)) throw lastErr;
    } catch (e) {
      if (!(e as any)._retryable) lastErr = e as Error;
    }
  }
  throw lastErr ?? new Error('unknown fetch error');
}

/**
 * Fire-and-poll — same pattern for Veo generate. Rows appear in
 * marketing.content_generated_video with status=running; we poll for
 * status=done | failed.
 */
/**
 * Preview mode — asks the edge fn to fuse the style + subject via Claude
 * and return the final Veo prompt WITHOUT actually generating anything.
 * Lets the user see what will hit Veo before spending credits.
 */
export function usePreviewPrompt() {
  return useMutation({
    mutationFn: async (input: { ref_id: string; subject?: string; prompt_override?: string; style_override?: string; long_form?: boolean; duration_s?: number }) => {
      return await fetchJsonWithRetry('/api/content/video-ref-generate', { ...input, preview_only: true, aspect_ratio: '9:16' }) as { ok: true; preview: true; prompt: string; base_prompt: string; prompt_source: string };
    },
  });
}

/**
 * Plan a multi-shot reel — Claude storyboards N coordinated ≤8s shots
 * from the extracted style + user subject. Returns the plan without
 * generating anything; UI then fires per-shot generations with reel_id.
 */
export function usePlanShots() {
  return useMutation({
    mutationFn: async (input: { ref_id: string; subject: string; target_seconds: number; style_override?: string }) => {
      return await fetchJsonWithRetry('/api/content/video-ref-plan-shots', input) as ReelPlan;
    },
  });
}

export function useGenerateFromRef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ref_id: string; subject?: string; prompt_override?: string; style_override?: string; aspect_ratio?: string; duration_s?: number; long_form?: boolean; reel_id?: string; shot_index?: number; shot_total?: number; shot_description?: string }) => {
      // Capture existing generated rows so we can detect the new one
      const { data: before } = await marketing.from('content_generated_video').select('id').eq('source_ref_id', input.ref_id);
      const beforeIds = new Set((before ?? []).map((r: any) => r.id));

      fetch('/api/content/video-ref-generate', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }).catch(() => { /* swallow */ });

      // Poll for up to 5 min (Veo pro at 60s can take ~4 min)
      const deadline = Date.now() + 300_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3_000));
        const { data } = await marketing.from('content_generated_video')
          .select('id,status,error,b2_video_key,duration_s,generated_ms,prompt')
          .eq('source_ref_id', input.ref_id)
          .order('created_at', { ascending: false })
          .limit(3);
        qc.invalidateQueries({ queryKey: ['generated-videos', input.ref_id] });
        const fresh = (data ?? []).find((r: any) => !beforeIds.has(r.id));
        if (fresh) {
          if (fresh.status === 'failed') throw new Error(fresh.error ?? 'generation failed');
          if (fresh.status === 'done' && fresh.b2_video_key) {
            return { ok: true, id: fresh.id, b2_video_key: fresh.b2_video_key, bytes: 0, duration_s: fresh.duration_s ?? 0, generated_ms: fresh.generated_ms ?? 0, prompt: fresh.prompt } as any;
          }
        }
      }
      throw new Error('generate timed out after 5 min — check Supabase logs');
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['generated-videos'] }); },
  });
}

export function useGeneratedVideos(refId: string | null) {
  return useQuery<GeneratedVideo[]>({
    queryKey: ['generated-videos', refId],
    enabled: !!refId,
    queryFn: async () => {
      if (!refId) return [];
      const { data, error } = await marketing
        .from('content_generated_video')
        .select('id,source_ref_id,prompt,b2_video_key,duration_s,aspect_ratio,model,status,error,generated_ms,created_at,finished_at,reel_id,shot_index,shot_total,shot_description')
        .eq('source_ref_id', refId)
        .order('created_at', { ascending: false })
        .limit(40);
      if (error) throw error;
      return (data ?? []) as GeneratedVideo[];
    },
    // Aggressive polling so multi-shot reels update as each 8s clip lands
    refetchInterval: 5_000,
    staleTime: 3_000,
  });
}

/**
 * Fire N shot generations for a reel in parallel. Doesn't await HTTP
 * responses (CF Pages 30s limit + each Veo call takes ~40s). Progress
 * is observed via useGeneratedVideos which polls the DB every 5s.
 */
export function useGenerateReel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ref_id: string;
      reel_id: string;
      shots: Array<{ shot_index: number; duration_s: number; prompt: string; beat: string }>;
      long_form?: boolean;
      style_override?: string;
    }) => {
      const total = input.shots.length;
      for (const shot of input.shots) {
        fetch('/api/content/video-ref-generate', {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            ref_id: input.ref_id,
            reel_id: input.reel_id,
            shot_index: shot.shot_index,
            shot_total: total,
            shot_description: shot.beat,
            prompt_override: shot.prompt,
            style_override: input.style_override,
            duration_s: shot.duration_s,
            aspect_ratio: '9:16',
            long_form: input.long_form === true,
          }),
        }).catch(() => { /* polling handles the outcome */ });
      }
      qc.invalidateQueries({ queryKey: ['generated-videos', input.ref_id] });
      return { ok: true as const, reel_id: input.reel_id, kicked: total };
    },
  });
}

export function igEmbedUrl(ref: Pick<VideoRef, 'source_url' | 'shortcode'>): string | null {
  if (!ref.shortcode) return null;
  const cleaned = ref.source_url.replace(/\/+$/, '');
  return `${cleaned}/embed/captioned`;
}
